"use client";
import React from "react";
import { Check, MapPin, User } from "lucide-react";
import NFTPrice from "./nft-price";
import LazyNFTMedia from "./lazyLoadImage";

type NFTAttribute = {
	trait_type: string;
	value: string;
};

type NFTMetadata = {
	name: string;
	image: string;
	attributes?: NFTAttribute[];
	[key: string]: any;
};

type PinLocation = {
	formattedAddress: string;
	[key: string]: any;
};

type SearchResultItemProps = {
	pin: {
		_id: string;
		nftMetaData: NFTMetadata[];
		pinLocation: PinLocation;
		[key: string]: any;
	};
};

const SearchResultItem: React.FC<SearchResultItemProps> = ({ pin }) => {
	const nft = pin.nftMetaData[0];
	const creatorName = pin.userDetails?.userName || "Anonymous";

	return (
		<div className="w-full p-3 bg-mgt-black/70 rounded-xl hover:bg-mgt-black/40 transition-colors duration-200 my-2">
			<div className="flex gap-3">
				<div className="relative w-16 h-16 flex-shrink-0">
					<LazyNFTMedia
						src={nft.image}
						alt={nft.name || "NFT Image"}
						isSelected={false}
						isCompressed={false}
						width={64}
						height={64}
						quality={85}
						loading="lazy"
						className="rounded-lg"
					/>
				</div>

				<div className="flex-1 min-w-0">
					<h3 className="text-white font-medium truncate">{nft.name}</h3>

					<div className="flex items-center gap-1.5 mt-1 min-w-0">
						<MapPin className="text-red-400 size-3.5 flex-shrink-0" />
						<span className="text-sm text-gray-300 truncate">
							{pin.pinLocation.city}, {pin.pinLocation.country}
						</span>
					</div>

					<NFTPrice
						priceInUSD={pin.stashDetails?.pricePerNft}
						containerClassName="mt-1"
						iconClassName="text-mgt-yellow size-3.5 flex-shrink-0"
						textClassName="text-sm text-gray-300 whitespace-nowrap"
					/>

					{nft.attributes && nft.attributes.length > 0 && (
						<div className="mt-2 flex flex-wrap gap-1.5">
							{nft.attributes.map((attr: NFTAttribute, index: number) => (
								<span
									key={index}
									className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-mgt-green text-white"
								>
									{attr.trait_type}: {attr.value}
								</span>
							))}
						</div>
					)}
				</div>
			</div>

			<div className="flex items-center justify-end gap-1.5 mt-2 pt-2 border-t border-white/10">
				{pin.userDetails?.image ? (
					<div className="relative h-5 w-5 flex-shrink-0">
						<LazyNFTMedia
							src={pin.userDetails.image}
							alt={creatorName}
							isSelected={false}
							width={20}
							height={20}
							quality={75}
							loading="lazy"
							className="rounded-full"
						/>
					</div>
				) : (
					<div className="h-5 w-5 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
						<User className="size-3 text-gray-600" />
					</div>
				)}
				<span className="text-gray-400 text-xs truncate max-w-[10rem]">
					{creatorName}
				</span>
				{pin.userDetails?.role === "admin" && (
					<div className="bg-mgt-green rounded-full p-0.5 flex items-center justify-center flex-shrink-0">
						<Check className="size-2.5 text-mgt-black" />
					</div>
				)}
			</div>
		</div>
	);
};

export default SearchResultItem;
